import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { useData } from "@/lib/useData";
import type { Activity, Contact, Deal, ActivityType } from "@/lib/types";
import { ACTIVITY_TYPES } from "@/lib/types";
import {
	Plus, MoreHorizontal, Pencil, Trash2, Search,
	Phone, Mail, Calendar, FileText, CheckSquare,
	Circle, CheckCircle2,
} from "lucide-react";

const typeIcons: Record<string, typeof Phone> = {
	call: Phone,
	email: Mail,
	meeting: Calendar,
	note: FileText,
	task: CheckSquare,
};

const typeColors: Record<string, string> = {
	call: "bg-blue-100 text-blue-800",
	email: "bg-green-100 text-green-800",
	meeting: "bg-purple-100 text-purple-800",
	note: "bg-amber-100 text-amber-800",
	task: "bg-rose-100 text-rose-800",
};

const emptyActivity = {
	type: "task" as ActivityType,
	subject: "",
	description: "",
	contactId: null as string | null,
	dealId: null as string | null,
	companyId: null as string | null,
	completed: false,
	dueDate: null as string | null,
};

export function ActivitiesPage() {
	const activitiesFetcher = useCallback(() => api.activities.list(), []);
	const contactsFetcher = useCallback(() => api.contacts.list(), []);
	const dealsFetcher = useCallback(() => api.deals.list(), []);

	const { data: activities, loading, refresh } = useData<Activity[]>(activitiesFetcher);
	const { data: contacts } = useData<Contact[]>(contactsFetcher);
	const { data: deals } = useData<Deal[]>(dealsFetcher);

	const [search, setSearch] = useState("");
	const [filter, setFilter] = useState<"all" | "open" | "completed">("all");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editing, setEditing] = useState<Activity | null>(null);
	const [form, setForm] = useState(emptyActivity);

	const contactMap = new Map(contacts?.map(c => [c.id, `${c.firstName} ${c.lastName}`]) ?? []);
	const dealMap = new Map(deals?.map(d => [d.id, d.title]) ?? []);

	const filtered = activities?.filter(a => {
		const q = search.toLowerCase();
		const matchesSearch = a.subject.toLowerCase().includes(q) || a.description.toLowerCase().includes(q);
		const matchesFilter =
			filter === "all" ? true :
			filter === "open" ? !a.completed :
			a.completed;
		return matchesSearch && matchesFilter;
	}) ?? [];

	function openCreate() {
		setEditing(null);
		setForm(emptyActivity);
		setDialogOpen(true);
	}

	function openEdit(activity: Activity) {
		setEditing(activity);
		setForm({
			type: activity.type,
			subject: activity.subject,
			description: activity.description,
			contactId: activity.contactId,
			dealId: activity.dealId,
			companyId: activity.companyId,
			completed: activity.completed,
			dueDate: activity.dueDate,
		});
		setDialogOpen(true);
	}

	async function handleSave() {
		if (editing) {
			await api.activities.update(editing.id, form);
		} else {
			await api.activities.create(form);
		}
		setDialogOpen(false);
		refresh();
	}

	async function handleDelete(id: string) {
		await api.activities.delete(id);
		refresh();
	}

	async function toggleComplete(activity: Activity) {
		await api.activities.update(activity.id, { completed: !activity.completed });
		refresh();
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Activities</h1>
					<p className="text-sm text-muted-foreground">{activities?.length ?? 0} total activities</p>
				</div>
				<Button onClick={openCreate} size="sm">
					<Plus className="h-4 w-4 mr-1" /> Add Activity
				</Button>
			</div>

			<Card>
				<CardHeader className="pb-3 space-y-3">
					<div className="relative">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
						<Input
							placeholder="Search activities..."
							value={search}
							onChange={e => setSearch(e.target.value)}
							className="pl-9"
						/>
					</div>
					<Tabs value={filter} onValueChange={v => setFilter(v as typeof filter)}>
						<TabsList>
							<TabsTrigger value="all">All</TabsTrigger>
							<TabsTrigger value="open">Open</TabsTrigger>
							<TabsTrigger value="completed">Completed</TabsTrigger>
						</TabsList>
					</Tabs>
				</CardHeader>
				<CardContent>
					{loading ? (
						<div className="p-6 text-center text-muted-foreground">Loading...</div>
					) : filtered.length === 0 ? (
						<div className="p-6 text-center text-muted-foreground">No activities found</div>
					) : (
						<div className="space-y-2">
							{filtered.map(activity => {
								const Icon = typeIcons[activity.type] ?? FileText;
								return (
									<div
										key={activity.id}
										className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
											activity.completed ? "bg-muted/50 opacity-60" : "hover:bg-muted/30"
										}`}
									>
										<button
											onClick={() => toggleComplete(activity)}
											className="mt-0.5 text-muted-foreground hover:text-foreground transition-colors"
										>
											{activity.completed ? (
												<CheckCircle2 className="h-5 w-5 text-green-600" />
											) : (
												<Circle className="h-5 w-5" />
											)}
										</button>
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-2">
												<p className={`text-sm font-medium ${activity.completed ? "line-through" : ""}`}>
													{activity.subject}
												</p>
												<Badge variant="secondary" className={`text-[10px] ${typeColors[activity.type]}`}>
													<Icon className="h-3 w-3 mr-1" />
													{ACTIVITY_TYPES.find(t => t.value === activity.type)?.label}
												</Badge>
											</div>
											{activity.description && (
												<p className="text-xs text-muted-foreground mt-1 line-clamp-1">{activity.description}</p>
											)}
											<div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
												{activity.contactId && (
													<span>{contactMap.get(activity.contactId)}</span>
												)}
												{activity.dealId && (
													<span>{dealMap.get(activity.dealId)}</span>
												)}
												{activity.dueDate && (
													<span>Due {new Date(activity.dueDate).toLocaleDateString()}</span>
												)}
											</div>
										</div>
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
													<MoreHorizontal className="h-4 w-4" />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end">
												<DropdownMenuItem onClick={() => openEdit(activity)}>
													<Pencil className="h-4 w-4 mr-2" /> Edit
												</DropdownMenuItem>
												<DropdownMenuItem className="text-destructive" onClick={() => handleDelete(activity.id)}>
													<Trash2 className="h-4 w-4 mr-2" /> Delete
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</div>
								);
							})}
						</div>
					)}
				</CardContent>
			</Card>

			{/* Create/Edit Dialog */}
			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{editing ? "Edit Activity" : "New Activity"}</DialogTitle>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>Type</Label>
								<Select value={form.type} onValueChange={v => setForm({ ...form, type: v as ActivityType })}>
									<SelectTrigger><SelectValue /></SelectTrigger>
									<SelectContent>
										{ACTIVITY_TYPES.map(t => (
											<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>Due Date</Label>
								<Input
									type="date"
									value={form.dueDate ?? ""}
									onChange={e => setForm({ ...form, dueDate: e.target.value || null })}
								/>
							</div>
						</div>
						<div className="space-y-2">
							<Label>Subject</Label>
							<Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
						</div>
						<div className="space-y-2">
							<Label>Description</Label>
							<Textarea
								value={form.description}
								onChange={e => setForm({ ...form, description: e.target.value })}
								rows={3}
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>Contact</Label>
								<Select
									value={form.contactId ?? "none"}
									onValueChange={v => setForm({ ...form, contactId: v === "none" ? null : v })}
								>
									<SelectTrigger><SelectValue placeholder="Select contact" /></SelectTrigger>
									<SelectContent>
										<SelectItem value="none">None</SelectItem>
										{contacts?.map(c => (
											<SelectItem key={c.id} value={c.id}>{c.firstName} {c.lastName}</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>Deal</Label>
								<Select
									value={form.dealId ?? "none"}
									onValueChange={v => setForm({ ...form, dealId: v === "none" ? null : v })}
								>
									<SelectTrigger><SelectValue placeholder="Select deal" /></SelectTrigger>
									<SelectContent>
										<SelectItem value="none">None</SelectItem>
										{deals?.map(d => (
											<SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
						<Button onClick={handleSave}>{editing ? "Save Changes" : "Create Activity"}</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
